import { z } from "zod";

// Client schemas
export const insertClientSchema = z.object({
  name: z.string().min(1, "Meno je povinné"),
  email: z.string().email("Neplatný email").min(1, "Email je povinný"),
  phone: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  businessName: z.string().optional(),
  businessId: z.string().optional(),
  taxId: z.string().optional(),
  vatId: z.string().optional(),
  hourlyRate: z.number().optional(),
  dueDays: z.number().optional(),
  invoiceEmail: z.string().email("Neplatný email pre faktúry").optional().or(z.literal("")),
  company: z.string().optional(),
  note: z.string().optional(),
  vatRate: z.string().optional(),
});

export const updateClientSchema = insertClientSchema.partial();

export type InsertClient = z.infer<typeof insertClientSchema>;
export type UpdateClient = z.infer<typeof updateClientSchema>;

// Client type for frontend
export type Client = {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  businessName?: string;
  businessId?: string;
  taxId?: string;
  vatId?: string;
  hourlyRate?: number;
  dueDays?: number;
  invoiceEmail?: string;
  company?: string;
  note?: string;
  vatRate?: string;
  createdAt: Date;
  updatedAt?: Date;
};

// Task schemas
export const insertTaskSchema = z.object({
  title: z.string().min(1, "Názov úlohy je povinný"),
  description: z.string().optional(),
  clientId: z.number().optional(),
  assignedToId: z.number().optional(),
  status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']).default('pending'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  taskType: z.enum(['standard', 'reminder', 'recurring']).default('standard'),
  dueDate: z.string().optional(),
  hourlyRate: z.number().optional(),
  estimatedHours: z.number().optional(),
});

export type InsertTask = z.infer<typeof insertTaskSchema>;

// Task type for frontend
export type Task = {
  subtotal_cents?: number;
  tax_cents?: number;
  total_cents?: number;
  id: number;
  title: string;
  description?: string;
  clientId?: number;
  assignedToId?: number;
  status: string;
  priority: string;
  taskType: string;
  dueDate?: string;
  hourlyRate?: number;
  estimatedHours?: number;
  actualHours?: number;
  totalAmount?: number;
  isStarred?: boolean;
  isInvoiced?: boolean;
  invoiceId?: number;
  clientName?: string;
  createdAt: Date;
  updatedAt?: Date;
};

// Invoice schemas
export const insertInvoiceSchema = z.object({
  clientId: z.number(),
  invoiceNumber: z.string(),
  issueDate: z.string(),
  dueDate: z.string(),
  status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']).default('draft'),
  subtotal: z.number(),
  taxAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string().default('EUR'),
  notes: z.string().optional(),
});

export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;

// Invoice type for frontend
export type Invoice = {
  id: number;
  clientId: number;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  notes?: string;
  clientName?: string;
  createdAt: Date;
  updatedAt?: Date;
};

// Price Quote schemas
export const insertPriceQuoteSchema = z.object({
  clientId: z.number(),
  quoteNumber: z.string(),
  issueDate: z.string(),
  dueDate: z.string(),
  status: z.enum(['draft', 'sent', 'accepted', 'rejected']).default('draft'),
  subtotal: z.number(),
  taxAmount: z.number(),
  totalAmount: z.number(),
  currency: z.string().default('EUR'),
  notes: z.string().optional(),
});

export type InsertPriceQuote = z.infer<typeof insertPriceQuoteSchema>;

// Price Quote type for frontend
export type PriceQuote = {
  id: number;
  clientId: number;
  quoteNumber: string;
  issueDate: string;
  dueDate: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  notes?: string;
  createdAt: Date;
  updatedAt?: Date;
};

export type PriceQuoteItem = {
  id: number;
  priceQuoteId: number;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

// User schemas
export const insertUserSchema = z.object({
  email: z.string().email("Neplatný email"),
  password: z.string().min(6, "Heslo musí mať aspoň 6 znakov"),
  name: z.string().min(1, "Meno je povinné"),
  role: z.enum(['user', 'admin', 'superadmin']).default('user'),
});

export const updateProfileSchema = z.object({
  name: z.string().optional(),
  email: z.string().email("Neplatný email").optional(),
  // Firemné údaje
  companyName: z.string().optional(),
  companyStreet: z.string().optional(),
  companyCity: z.string().optional(),
  companyPostalCode: z.string().optional(),
  companyCountry: z.string().optional(),
  companyRegNumber: z.string().optional(),
  companyTaxId: z.string().optional(),
  companyVatId: z.string().optional(),
  companyVatRate: z.union([z.number(), z.string()]).optional(),
  isVatPayer: z.boolean().optional(),
  // Bankové údaje
  bankAccount: z.string().optional(),
  bankIban: z.string().optional(),
  bankSwift: z.string().optional(),
  // Nastavenia faktúr
  invoiceNumberFormat: z.string().optional(),
  invoiceFooterText: z.string().optional(),
  invoicePrimaryColor: z.string().optional(),
  invoiceStartingNumber: z.number().optional(),
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpdateProfile = z.infer<typeof updateProfileSchema>;

// User type for frontend
export type User = {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt?: Date;
};

// Login schema
export const loginSchema = z.object({
  email: z.string().email("Neplatný email"),
  password: z.string().min(1, "Heslo je povinné"),
});

export type LoginData = z.infer<typeof loginSchema>;

// Team Member schemas
export const insertTeamMemberSchema = z.object({
  userId: z.number(),
  invitedEmail: z.string().email("Neplatný email"),
  memberName: z.string().min(1, "Meno je povinné"),
  role: z.enum(['employee', 'admin']).default('employee'),
});

export type InsertTeamMember = z.infer<typeof insertTeamMemberSchema>;

// Team Member type for frontend
export type TeamMember = {
  id: number;
  userId: number;
  invitedEmail: string;
  memberName: string;
  role: string;
  invitedAt: Date;
  acceptedAt?: Date;
};

// Subscription Plan types
export type SubscriptionPlan = {
  id: number;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingInterval: string;
  features?: any;
  isActive: boolean;
  paddlePlanId?: string;
  createdAt: Date;
  updatedAt?: Date;
};

// Admin types
export type ActivityLog = {
  id: number;
  userId: number;
  action: string;
  details: string;
  createdAt: Date;
};

export type PlanHistory = {
  id: number;
  userId: number;
  planId: number;
  action: string;
  createdAt: Date;
};

export type SystemMessage = {
  id: number;
  title: string;
  message: string;
  type: string;
  createdAt: Date;
};

// System Settings
export type SystemSetting = {
  id: number;
  key: string;
  value: any;
  type: string;
  isPublic: boolean;
  description?: string;
  createdAt: Date;
  updatedAt?: Date;
};

// Plan Management
export type PlanDetail = {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingInterval: string;
  features: any;
  isActive: boolean;
};

export type UpdatePlanDetail = Partial<PlanDetail>;

// Dobropis types
export type Dobropis = {
  id: number;
  userId: number;
  clientId: number;
  relatedInvoiceId?: number;
  creditNoteNumber: string;
  issueDate: string;
  status: string;
  amount: string;
  createdAt: Date;
  updatedAt?: Date;
};

export type InsertDobropis = {
  userId: number;
  clientId: number;
  relatedInvoiceId?: number;
  creditNoteNumber: string;
  issueDate: string;
  status: string;
  amount: string;
};
